/**
 * The customer bot's dashboard API, `/admin/v1`.
 *
 * Kept apart from `client.ts` on purpose: that file is the Agents Assistant's, this one is the
 * customer bot's, and the two subsystems have different contracts, different versions and
 * different reasons to change. One file that grew both would be edited by everybody.
 *
 * ## The shapes here are the snapshot's shapes
 *
 * `docs/m2/config.seed.json` is what NAB hands us, what the dashboard writes and what the bot
 * reads — one format for all three. So these types are that document, and a screen that renders a
 * tool is rendering the same object the bot loads. There is no view model in between to drift.
 */

import { ApiError, type ApiOptions } from "./client";

// ---------------------------------------------------------------------------------------
// The config, as the snapshot holds it
// ---------------------------------------------------------------------------------------

/** A per-language map: `{"en": "...", "ly-latn": "...", "ly-arab": "..."}`. */
export type Localized = Record<string, string>;

export interface Requirement {
  field: string;
  type: "string" | "enum" | "integer" | "money" | "date" | "boolean";
  required?: boolean;
  default?: unknown;
  values?: string[];
  min?: string | number;
  max?: number;
  notBefore?: string;
  currency?: string;
  decimals?: number;
}

/**
 * How long a tool's `details` may be before publish validation refuses it.
 *
 * Mirrors `customer_ai.nlu.router.MAX_DETAILS_CHARS`, which is the number the validator uses.
 * Here so the editor can say the limit while it is being typed rather than at publish.
 */
export const MAX_TOOL_DETAILS = 400;

export interface BotTool {
  action: string;
  tier: "read" | "lookup" | "transactional";
  active: boolean;
  requiresOtp?: boolean;
  sort?: number;
  routerDescription: string;
  /**
   * When this tool is the right one and when it is not, for the router model.
   *
   * Optional, English, and read on every turn — see `MAX_TOOL_DETAILS`.
   */
  details?: string;
  /** Few-shots for the router, keyed by language (not language-script). */
  examples: Record<string, string[]>;
  requirements: Requirement[];
  clarifications?: Record<string, Localized>;
  outcomes: string[];
  messages: Record<string, Localized>;
  display: { card?: string; quickReplies?: Record<string, string[]> | string[] };
}

export interface BotPlaceholder {
  name: string;
  type: string;
  description: string;
  /** What the preview substitutes. Never sent to a customer. */
  example: string;
  format?: Record<string, unknown>;
}

export interface BotOffer {
  offerId: string;
  topics: string[];
  approved: boolean;
  headline: Localized;
  deepLink?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
}

export interface BotSettings {
  languages?: string[];
  scripts?: Record<string, string[]>;
  templateFallback?: Record<string, string[]>;
  session?: { ttlMinutes?: number; maxHistoryTurns?: number };
  router?: {
    minConfidence?: number;
    maxClarifications?: number;
    coarseGate?: boolean;
    coarseGateThreshold?: number;
  };
  knowledge?: {
    topK?: number;
    minRerankScore?: number;
    maxAnswerSentences?: number;
    /** Return a question-and-answer entry in the bank's own words instead of the model's. */
    verbatimFaq?: boolean;
    /** The cosine similarity an entry must reach first. Above the retrieval gate's own 0.80. */
    verbatimMinScore?: number;
  };
  offers?: { enabled?: boolean; frequencyCapTurns?: number };
  /** Where a customer the bot could not help is sent. Absent means nowhere, which is allowed. */
  handoff?: { type?: "call_center" | "branch" | "agent"; value?: string | null };
  /** What the bot offers when it has no answer — per language tag, not per tool. */
  starterChips?: Record<string, string[]>;
  tone?: Record<string, string>;
  [key: string]: unknown;
}

export interface BotVersion {
  id: number;
  label: string;
  status: "draft" | "pending_approval" | "published" | "archived";
  notes: string | null;
  createdBy: string;
  createdAt: string;
  submittedBy: string | null;
  approvedBy: string | null;
  publishedBy: string | null;
  publishedAt: string | null;
  checksum: string | null;
  /**
   * Which sensitive areas this draft changes. Non-empty means Publish will be refused without a
   * second approver — shown *before* anyone presses it, rather than as a 403 afterwards.
   */
  needsApproval: string[];
}

export interface BotConfig {
  version: BotVersion;
  settings: BotSettings;
  placeholders: BotPlaceholder[];
  tools: BotTool[];
  phrases: Record<string, Localized>;
  offers: BotOffer[];
}

export interface ValidationFinding {
  /** A dotted path into the snapshot, which the UI turns into a link to the cell. */
  where: string;
  message: string;
}

export interface ValidationReport {
  publishable: boolean;
  errors: ValidationFinding[];
  warnings: ValidationFinding[];
}

/** What loading a file of tools would do, or did. See `docs/m2/tools-file.md`. */
export interface ToolsFileResult {
  /** Read this first. False with an empty `blocking` is a dry run; false with findings is a refusal. */
  applied: boolean;
  mode: "merge" | "replace";
  /** The draft the tools landed in, or would land in. */
  version: string | null;
  creates: string[];
  replaces: string[];
  unchanged: string[];
  deletes: string[];
  /** Why the file could not be loaded at all. Non-empty means nothing was written — the whole file. */
  blocking: ValidationFinding[];
  /** What the publish rules say about the result. Reported, never blocking. */
  errors: ValidationFinding[];
  warnings: ValidationFinding[];
}

export interface VersionDiff {
  from_label: string;
  to_label: string;
  changes: { path: string; kind: "added" | "removed" | "changed"; before: unknown; after: unknown }[];
}

export interface TemplatePreview {
  filled: string;
  used: string[];
  /** Names no placeholder declares. Shown in red beside the cell being typed in. */
  undeclared: string[];
}

export interface PlaygroundTurn {
  response: Record<string, unknown>;
  diagnostics: {
    intent: string | null;
    validation: string | null;
    routerConfidence: number | null;
    routerFailed: boolean | null;
    language: string;
    script: string;
    collectionsSearched: string[];
    pending: Record<string, unknown> | null;
    maskedMessage: string;
    promptSentToModel: {
      message: string;
      history: string[];
      pendingTrigger: string | null;
    } | null;
  };
}

export interface AuditEntry {
  id: number;
  at: string;
  actor: string;
  action: string;
  entity: string;
  versionId: number | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------------------

const BASE = "/admin/v1";

async function request<T>(
  path: string,
  options: ApiOptions & { method?: string; body?: unknown } = {},
): Promise<T> {
  const { method = "GET", body, baseUrl, signal, ...identity } = options;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  // Development identity. In a deployed environment the session cookie carries it and the server
  // ignores these, so sending them costs nothing and keeps local work simple.
  if (identity.employeeId) headers["X-NAB-Employee-Id"] = identity.employeeId;
  if (identity.role) headers["X-NAB-Role"] = identity.role;

  const response = await fetch(`${baseUrl ?? "/api"}${BASE}${path}`, {
    method,
    headers,
    credentials: "same-origin",
    signal: signal ?? null,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!response.ok) {
    let detail: unknown;
    try {
      detail = (await response.json())?.detail;
    } catch {
      /* a non-JSON error body; the status carries the meaning */
    }
    throw new ApiError(`Request failed with ${response.status}`, response.status, detail);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------------------

/** The draft if there is one, otherwise what is live. Every screen starts here. */
export const fetchBotConfig = (o?: ApiOptions): Promise<BotConfig> =>
  request<BotConfig>("/config", o);

export const fetchBotVersions = (o?: ApiOptions): Promise<{ versions: BotVersion[] }> =>
  request("/versions", o);

export const fetchCurrentVersions = (
  o?: ApiOptions,
): Promise<{ published: BotVersion | null; draft: BotVersion | null }> =>
  request("/versions/current", o);

export const fetchVersionDiff = (
  from: number,
  to: number,
  o?: ApiOptions,
): Promise<VersionDiff> => request(`/versions/${from}/diff/${to}`, o);

export const fetchBotAudit = (o?: ApiOptions): Promise<{ entries: AuditEntry[] }> =>
  request("/audit", o);

// ---------------------------------------------------------------------------------------
// Writing — always onto the draft, which the server creates on demand
// ---------------------------------------------------------------------------------------

export const saveTool = (tool: BotTool, o?: ApiOptions) =>
  request<{ saved: string; version: string }>(`/tools/${encodeURIComponent(tool.action)}`, {
    ...o,
    method: "PUT",
    body: tool,
  });

/**
 * What is wrong with a tool that has not been saved yet.
 *
 * The publish rules, run server-side against the draft with this candidate substituted in. Not a
 * copy of them here: a tool is valid only relative to the config around it — `{{reference}}` is a
 * declared placeholder or it is not, "every language" is whatever settings enables — and a second
 * implementation would disagree with the one that blocks publishing.
 *
 * Writes nothing, so it is safe to call while somebody types.
 */
export const validateTool = (tool: BotTool, o?: ApiOptions): Promise<ValidationReport> =>
  request(`/tools/${encodeURIComponent(tool.action)}/validate`, {
    ...o,
    method: "POST",
    body: tool,
  });

/**
 * Load a whole file of tools onto the draft.
 *
 * `apply: false` is a dry run — the same parse, the same ordering, the same publish rules, nothing
 * written, not even a draft. The screen always asks for that first, because a bulk edit nobody
 * previewed is a bulk edit nobody can check and `replace` mode deletes.
 *
 * `document` is the file's own JSON, unchanged. Not a shape built here: the file's format is the
 * snapshot's format, so an export is a valid import and there is nothing to translate.
 */
export const importTools = (
  document: unknown,
  options: { mode?: "merge" | "replace"; apply?: boolean } = {},
  o?: ApiOptions,
): Promise<ToolsFileResult> =>
  request("/tools/import", {
    ...o,
    method: "POST",
    body: { document, mode: options.mode ?? "merge", apply: options.apply ?? false },
  });

export const deleteTool = (action: string, o?: ApiOptions) =>
  request<{ deleted: string }>(`/tools/${encodeURIComponent(action)}`, {
    ...o,
    method: "DELETE",
  });

export const savePlaceholder = (placeholder: BotPlaceholder, o?: ApiOptions) =>
  request<{ saved: string; version: string }>(
    `/placeholders/${encodeURIComponent(placeholder.name)}`,
    { ...o, method: "PUT", body: placeholder },
  );

export const deletePlaceholder = (name: string, o?: ApiOptions) =>
  request<{ deleted: string }>(`/placeholders/${encodeURIComponent(name)}`, {
    ...o,
    method: "DELETE",
  });

export const savePhrase = (key: string, texts: Localized, o?: ApiOptions) =>
  request<{ saved: string; version: string }>(`/phrases/${encodeURIComponent(key)}`, {
    ...o,
    method: "PUT",
    body: { key, texts },
  });

export const saveOffer = (offer: BotOffer, o?: ApiOptions) =>
  request<{ saved: string; version: string }>(
    `/offers/${encodeURIComponent(offer.offerId)}`,
    { ...o, method: "PUT", body: offer },
  );

export const deleteOffer = (offerId: string, o?: ApiOptions) =>
  request<{ deleted: string }>(`/offers/${encodeURIComponent(offerId)}`, {
    ...o,
    method: "DELETE",
  });

export const saveSetting = (key: string, value: unknown, o?: ApiOptions) =>
  request<{ saved: string; version: string }>(`/settings/${encodeURIComponent(key)}`, {
    ...o,
    method: "PUT",
    body: { key, value },
  });

// ---------------------------------------------------------------------------------------
// The version lifecycle
// ---------------------------------------------------------------------------------------

export const validateVersion = (id: number, o?: ApiOptions): Promise<ValidationReport> =>
  request(`/versions/${id}/validate`, { ...o, method: "POST" });

export const submitVersion = (id: number, o?: ApiOptions): Promise<BotVersion> =>
  request(`/versions/${id}/submit`, { ...o, method: "POST" });

export const approveVersion = (id: number, o?: ApiOptions): Promise<BotVersion> =>
  request(`/versions/${id}/approve`, { ...o, method: "POST" });

export const publishVersion = (
  id: number,
  o?: ApiOptions,
): Promise<BotVersion & { serving: string | null }> =>
  request(`/versions/${id}/publish`, { ...o, method: "POST" });

export const rollbackVersion = (
  id: number,
  o?: ApiOptions,
): Promise<BotVersion & { serving: string | null }> =>
  request(`/versions/${id}/rollback`, { ...o, method: "POST" });

export const discardDraft = (id: number, o?: ApiOptions) =>
  request<{ discarded: string }>(`/versions/${id}`, { ...o, method: "DELETE" });

// ---------------------------------------------------------------------------------------
// Preview and playground
// ---------------------------------------------------------------------------------------

export const previewTemplate = (
  template: string,
  values?: Record<string, string>,
  o?: ApiOptions,
): Promise<TemplatePreview> =>
  request("/templates/preview", {
    ...o,
    method: "POST",
    body: { template, values: values ?? {} },
  });

export const playgroundChat = (
  body: {
    message: string;
    sessionId?: string | null;
    history?: unknown[];
    against?: "draft" | "published";
  },
  o?: ApiOptions,
): Promise<PlaygroundTurn> =>
  request("/playground/chat", { ...o, method: "POST", body });

// ---------------------------------------------------------------------------------------
// The developer console
// ---------------------------------------------------------------------------------------

export interface TurnStage {
  name: string;
  ms: number;
  /** A verdict or a count — never customer data. See `customer_ai/engine/stages.py`. */
  detail: string;
}

export interface DevTurn {
  auditId: string;
  at: string;
  sessionId: string;
  /** The masked form, which is the only form the audit store ever held. */
  message: string | null;
  language: string | null;
  script: string | null;
  sessionReset: boolean | null;
  intent: string | null;
  nextStep: string | null;
  tier: string | null;
  triggerId: string | null;
  parameters: Record<string, unknown> | null;
  validation: string | null;
  refusalReason: string | null;
  routerConfidence: number | null;
  routerFailed: boolean | null;
  collectionsSearched: string[];
  offerId: string | null;
  configVersion: string | null;
  requestId: string | null;
  latencyMs: number | null;
  stages: TurnStage[];
  slowestStage: string | null;
  /** Which dependencies the turn actually reached, derived from its stages. */
  systems: string[];
}

export interface DevSummary {
  minutes: number;
  turns: number;
  byNextStep: Record<string, number>;
  byIntent: Record<string, number>;
  refusalRate: number;
  p50LatencyMs: number | null;
  p95LatencyMs: number | null;
}

export interface SystemCheck {
  up: boolean | null;
  detail?: string;
  status?: number;
  endpoint?: string;
  profile?: string;
  collections?: string[];
  customerCollection?: string;
  version?: string;
  checksum?: string;
}

export interface DevSystems {
  checks: Record<string, SystemCheck>;
  allUp: boolean;
}

export const fetchDevTurns = (
  params: { limit?: number; sinceId?: string; nextStep?: string; intent?: string } = {},
  o?: ApiOptions,
): Promise<{ turns: DevTurn[] }> => {
  const query = new URLSearchParams();
  if (params.limit) query.set("limit", String(params.limit));
  if (params.sinceId) query.set("since_id", params.sinceId);
  if (params.nextStep) query.set("next_step", params.nextStep);
  if (params.intent) query.set("intent", params.intent);
  const suffix = query.toString() ? `?${query}` : "";
  return request(`/dev/turns${suffix}`, o);
};

export const fetchDevSummary = (minutes = 60, o?: ApiOptions): Promise<DevSummary> =>
  request(`/dev/summary?minutes=${minutes}`, o);

export const fetchDevSystems = (o?: ApiOptions): Promise<DevSystems> =>
  request("/dev/systems", o);

/**
 * Where the Postman collection downloads from.
 *
 * A URL rather than a fetch: the response carries `Content-Disposition`, so letting the browser
 * follow the link gives the file its name and the save dialog for free. Fetching it into a blob
 * would mean reimplementing both.
 *
 * It is still an authenticated request — the link is same-origin and the session cookie rides
 * along, which is the whole reason the console lives behind this sign-in.
 */
export const postmanCollectionUrl = (baseUrl: string, o?: ApiOptions): string =>
  `${o?.baseUrl ?? "/api"}${BASE}/dev/postman?base_url=${encodeURIComponent(baseUrl)}`;
